import { type HTMLAttributes, memo, useMemo } from 'react';

import { useGetValorantAgentById } from '@/features/games/valorant/hooks/useGetValorantAgents';
import { getAgentIconUrl } from '@/features/games/valorant/utils/agents';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { cn } from '@/shared/utils/cn';

export interface ValorantAgentsDisplayProps extends HTMLAttributes<HTMLDivElement> {
  agents?: { id: string; count: number }[];
  mainValorantAgentId?: string;
}

interface ValorantAgentItemProps {
  agentId: string;
  count: number;
  totalMatches: number;
  isMain: boolean;
}

const ValorantAgentItem = memo(function ValorantAgentItem({
  agentId,
  count,
  totalMatches,
  isMain,
}: ValorantAgentItemProps) {
  const agent = useGetValorantAgentById(agentId);

  const percentage =
    totalMatches > 0 ? Math.round((count / totalMatches) * 100) : 0;

  if (!agent) {
    return (
      <div className="bg-cold-800/20 size-16 rounded-xl border border-white/24" />
    );
  }

  return (
    <>
      {isMain && (
        <Badge variant="secondary" className="absolute z-1">
          Main
        </Badge>
      )}
      <Avatar className="size-16 rounded-xl border border-white/24">
        <AvatarImage
          src={getAgentIconUrl(agent.uuid)}
          alt={agent.displayName}
        />
        <AvatarFallback className="rounded-xl">
          {agent.displayName.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <Badge
        variant="default"
        className="z-1 -mt-2 rounded-full px-1 font-semibold"
      >
        {percentage}%
      </Badge>
    </>
  );
});

export const ValorantAgentsDisplay = memo(function ValorantAgentsDisplay(
  props: ValorantAgentsDisplayProps,
) {
  const { agents = [], mainValorantAgentId, className, ...rest } = props;

  const totalMatches = useMemo(
    () => agents.reduce((sum, agent) => sum + agent.count, 0),
    [agents],
  );

  const displayAgents: ((typeof agents)[0] | null)[] = [
    ...agents.slice(0, 4),
    ...new Array<null>(Math.max(0, 4 - agents.length)).fill(null),
  ];

  return (
    <div className={cn('flex justify-between', className)} {...rest}>
      {displayAgents.map((agentData, index) => (
        <div
          key={agentData?.id ?? `empty-${index.toString()}`}
          className="relative flex flex-col items-center"
        >
          {agentData ? (
            <ValorantAgentItem
              agentId={agentData.id}
              count={agentData.count}
              totalMatches={totalMatches}
              isMain={agentData.id === mainValorantAgentId}
            />
          ) : (
            <div className="bg-cold-800/20 size-16 rounded-xl border border-white/24" />
          )}
        </div>
      ))}
    </div>
  );
});
